/**
 * ONE SECTION OF AN OPTIONS PANEL.
 *
 * A panel is a sequence of named groups — Answer Format, Student Display, Measurement, Feedback —
 * and until this existed that sequence was a convention rather than a structure: each block
 * emitted a bare `<p>` heading followed by loose rows, all flat siblings of the panel. Nothing in
 * the markup said which rows belonged to which heading.
 *
 * That cost real controls. The phone-landscape panel is 288px wide in 651px of room, so the
 * obvious fix for a panel too tall to fit was to flow it into two columns — but a column break
 * lands between SIBLINGS, and the siblings were rows. Measured live: Slider's second column
 * opened with four Student Display rows whose heading had stayed at the foot of column one, and
 * Compass's opened with "Allow manual bypass" sitting directly above a heading it does not belong
 * to. Numerical happened to break cleanly, which is luck of row counts, not a layout.
 *
 * So a section is an element, and `break-inside-avoid` makes it indivisible. A column break can
 * now only fall between sections, where it carries its own heading with it.
 *
 * ## The separator belongs to the LAYOUT, not to the heading
 *
 * The rule between sections used to be `border-t` on every heading after the first. A heading
 * cannot know whether it is the first thing in a column, so in two columns that same rule drew a
 * stray line across the top of column two. The container now draws it — see
 * `OPTIONS_PANEL_SINGLE_COLUMN` — which is the only place that knows.
 *
 * ## Why `role='group'` and not a heading
 *
 * The panel is a flyout, not a document: four `region` landmarks or four `<h3>`s inside one
 * popover is noise. A named group is what this actually is, and it announces the heading once on
 * entry instead of leaving the rows unattributed.
 */
import { useId } from 'react';

import type { ReactNode } from 'react';

/**
 * The heading treatment, shared so a thirteenth panel cannot invent a different one.
 *
 * EXPORTED for the surfaces that name a group without being an options panel — the reaction
 * workbench labels its fragment rows and its atom controls with it. They are the same thing a
 * section heading is (a name over a run of controls) and they were 10px grey text, which the
 * owner read as the panel being *"really tiny"*. Use the component where a panel is laid out
 * in sections; use this where a heading is all that is wanted.
 */
const HEADING_BASE = 'font-semibold uppercase tracking-wide text-ws-primary';

export const HEADING_CLASS = `text-xs ${HEADING_BASE}`;

/**
 * The same heading one step up, for a bar that is not one section among several.
 *
 * A sibling constant rather than a size passed through `className`, because two Tailwind
 * utilities setting one property race in the generated stylesheet and the loser is decided by
 * source order -- silently, and differently in dev and in a production build. `IconButton`'s
 * `field` size carries the same note for the same reason.
 *
 * For the Preview debug panel's anchored attempts bar: it is the panel's foot rather than a peer
 * of the sections above it, it is the one part that never scrolls away, and at `text-xs` its
 * heading read as a label on the two buttons beside it.
 */
export const HEADING_CLASS_LG = `text-sm ${HEADING_BASE}`;

/**
 * A LABEL IN THIS DIALOG, which is not the same thing as a heading in an options panel.
 *
 * The workbench was built with `HEADING_CLASS` — uppercase, tracked, `ws-primary` — which is
 * right for the section headings of an options panel and wrong here, and the owner felt it
 * before naming it: *"i think we need to change the labels in structures to match labels used
 * elsewhere in the modal ... that's why it feels a little off"*.
 *
 * The reaction dialog's own register is sentence case and muted: its one visible row label,
 * `Category`, is `text-xs font-semibold text-ws-muted`, and the Quantities panel beside this
 * one carries no visible labels at all. Six uppercase primary headings in the middle of that
 * read as a different product.
 *
 * It keeps the SIZE that answered *"everything here is really tiny"* — `text-xs` and semibold
 * are the same as the heading it replaces. Only the shouting goes.
 *
 * It lives beside `HEADING_CLASS` because the two are the same question answered for two
 * surfaces, and a reader choosing between them wants to find them together. It moved here
 * from the structure editor when the marks list also needed it: the editor imports that
 * list and the list needed the label, which is a cycle wherever the constant sits in it.
 */
export const PANEL_LABEL = 'text-xs font-semibold text-ws-muted';

/**
 * What a panel laid out in ONE column puts on its container.
 *
 * The separator is a sibling rule rather than a property of the section, because "is there a
 * section above me" is a question only the container can answer. In one column DOM order is
 * visual order, so the sibling selector is exact; in two columns it is not, which is why the
 * two-column container below deliberately has no rule at all and lets the gutter separate them.
 */
export const OPTIONS_PANEL_SINGLE_COLUMN =
  'flex flex-col gap-3.5 [&>*+*]:border-t [&>*+*]:border-ws-border [&>*+*]:pt-3.5';

/**
 * What a panel laid out in TWO columns puts on its container.
 *
 * `columns` needs a block container, so the flex gap does not apply and the spacing moves onto
 * the sections themselves. Width is the caller's business — this says how the sections flow, not
 * how much room they get.
 */
// The LAST section's bottom margin is dropped: it is the one gap with nothing after it, and it
// was enough on its own to push Slider's panel past the cap and give it a 5px scrollbar for no
// content. The last DOM child is the foot of the final column, which is exactly the edge meant.
export const OPTIONS_PANEL_TWO_COLUMN = 'block columns-2 gap-5 [&>*]:mb-3.5 [&>*:last-child]:mb-0';

/**
 * How wide ONE column of a two-column panel may be, in px, and how narrow.
 *
 * The maximum is the one-column panel's own content width (`w-72` is 288 outside `px-4`, so 256
 * inside), so a column never reads wider than a panel does today and long labels wrap exactly
 * where they already do — Numerical needs all of it for "Require scientific notation (1.4e2)".
 *
 * But most panels do not. GPS's rows are "Live reading", "Capture", "Capture on submit"; held at
 * 256 they left most of each column empty, which is what the owner reported: *"for the others,
 * it leaves a lot of white space."* So the width is MEASURED from the content and only clamped
 * here. The floor keeps a panel of very short rows from collapsing into two slivers with the
 * gutter doing more work than the text.
 */
export const COLUMN_WIDTH_MAX = 256;
export const COLUMN_WIDTH_MIN = 150;

/** Gutter (`gap-5`) plus the panel's own `px-4`, which a column width has to be padded back by. */
export const TWO_COLUMN_CHROME = 20 + 32;

export function OptionsSection({ title, children }: {
  /** The section's name, already translated. */
  title: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    // `break-inside-avoid` is the whole point of the element: it is what makes a section the unit
    // a column break may fall between, rather than a run of rows a break may fall inside.
    // `data-options-section` is how the LAYOUT finds these, deliberately not `[role=group]`:
    // several controls inside a panel are groups in their own right (Scale's drawer holds five),
    // so counting by role would count them too and the frame would think a one-section panel had
    // something to split.
    <div data-options-section role='group' aria-labelledby={id} className='flex break-inside-avoid flex-col gap-3.5'>
      <p id={id} className={HEADING_CLASS}>{title}</p>
      {children}
    </div>
  );
}
